import { CheckboxList } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { TextInput } from "risa-oasis-ui_v2";

export interface AsyncSearchSelectOption {
  label: string;
  value: string;
}

export interface AsyncSearchSelectProps {
  options: AsyncSearchSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  onSearch: (query: string) => void;
  loading?: boolean;
  error?: string;
  placeholder?: string;
  label?: string;
  minSearchLength?: number;
  emptyMessage?: string;
  id?: string;
  renderFooter?: () => React.ReactNode;
  /** When true, Enter commits the typed text as a selected value without picking a dropdown option. */
  isTypeable?: boolean;
}

export function AsyncSearchSelect({
  options,
  value,
  onChange,
  onSearch,
  loading = false,
  error,
  placeholder = "Type to search...",
  label = "",
  minSearchLength = 2,
  emptyMessage = "No options found.",
  id = "async-search-select",
  renderFooter,
  isTypeable = false,
}: AsyncSearchSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [inputResetKey, setInputResetKey] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = useCallback(
    (data: { value?: string }) => {
      const q = data.value ?? "";
      setInputValue(q);
      onSearch(q);
      if (q.trim().length >= minSearchLength) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    },
    [onSearch, minSearchLength],
  );

  const handleCheckboxChange = useCallback(
    (
      items: { id: string; label: string; checked: boolean }[],
      changedId: string,
    ) => {
      const newValue = items.filter((i) => i.checked).map((i) => i.id);
      onChange(newValue);
    },
    [onChange],
  );

  /** Commits the current input text as a selected value when `isTypeable` is enabled. */
  const commitTypedValue = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed === "") return;
    if (!value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
    setIsOpen(false);
    setInputResetKey((k) => k + 1);
  }, [inputValue, value, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isTypeable || e.key !== "Enter") return;
      e.preventDefault();
      commitTypedValue();
    },
    [isTypeable, commitTypedValue],
  );

  const checkboxItems = options.map((opt) => ({
    id: opt.value,
    label: opt.label,
    checked: value.includes(opt.value),
  }));

  const showDropdown = isOpen && !loading;
  const showEmpty =
    showDropdown &&
    options.length === 0 &&
    inputValue.trim().length >= minSearchLength;

  return (
    <div ref={containerRef} className="relative w-full">
      {label ? (
        <div className="mb-1 text-sm font-medium text-primaryGray-1">
          {label}
        </div>
      ) : null}
      <div onKeyDown={isTypeable ? handleKeyDown : undefined}>
        <TextInput
          key={`${id}-${inputResetKey}`}
          id={id}
          label="Search and select value"
          placeholder={placeholder}
          defaultValue={inputValue}
          onChange={handleInputChange}
        />
      </div>
      {loading && (
        <div className="mt-1 text-sm text-primaryGray-7">Searching...</div>
      )}
      {error && <div className="mt-1 text-sm text-red-600">{error}</div>}

      {showDropdown && (options.length > 0 || showEmpty) && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-primaryGray-15 bg-white shadow-lg"
          role="listbox"
        >
          {options.length > 0 ? (
            <CheckboxList
              items={checkboxItems}
              onSelectionChange={handleCheckboxChange}
              showSelectAll={false}
              width="100%"
              className="max-h-60 overflow-auto"
            />
          ) : (
            <div className="px-3 py-3 text-sm text-primaryGray-7">
              {emptyMessage}
            </div>
          )}
          {renderFooter?.()}
        </div>
      )}
      {!showDropdown && renderFooter?.()}
    </div>
  );
}
